// Outbound-webhooks data layer (#807). Reads `GET /v1/webhooks` +
// `GET /v1/webhooks/deliveries` and drives create / delete / test over
// `apiFetch`. The API returns paged rows (masked url, health bool, the
// delivery ledger); the per-card telemetry (success %, retrying count, relative
// last-delivery) is derived HERE by grouping the one deliveries read by webhook
// — the shape `WebhookCard` / `DeliveryLog` render. Mirrors lib/emailRoutes.ts.

import { createQuery } from '@tanstack/solid-query';
import type { Delivery, WebhookHealth, WebhookRow } from '../routes/AccountWebhooks';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { canOwnerWrite } from './roles';
import { SESSION_TOKEN } from './session';
import { ago } from './time';

/** One webhook as the API lists it — masked url, health bool, never the secret. */
export type ApiWebhook = {
  readonly id: string;
  readonly name: string;
  readonly url_masked: string;
  readonly events: readonly string[];
  readonly healthy: boolean;
  readonly last_delivery_at: string | null;
};

/** One delivery-ledger row as the API returns it (flat, newest-first). */
export type ApiDelivery = {
  readonly id: string;
  readonly webhook_id: string;
  readonly webhook_name: string;
  readonly event_id: string;
  readonly type: string;
  readonly status_code: number | null;
  readonly attempt: number;
  readonly payload: unknown;
  readonly body_excerpt: string | null;
  readonly delivered_at: string | null;
  readonly next_retry_at: string | null;
  readonly created_at: string;
};

export const WEBHOOKS_QUERY_KEY = 'webhooks';
export const WEBHOOK_DELIVERIES_QUERY_KEY = 'webhook_deliveries';

/** ONE page of the account's webhooks at `cursor`. Idle with no session, like
 * the other feature reads. Hand it to `usePagedQuery` ({@link useWebhooks}). */
export function useWebhookPage(cursor: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<Page<ApiWebhook>>(() => ({
    queryKey: [WEBHOOKS_QUERY_KEY, token, activeAccountId(), cursor()],
    enabled: token !== null,
    queryFn: async () => {
      const page = await apiFetch<Page<ApiWebhook>>(withCursor('/v1/webhooks', cursor()), token);
      return { data: page.data, next_cursor: page.next_cursor ?? null };
    },
  }));
}

/**
 * The account's webhooks, walked WHOLE (`exhaust`, the `lib/repos.ts` idiom).
 * The route pages at the grammar's default 50 and mints `next_cursor`; reading
 * page one only (19-16) made a fifty-first webhook unreachable, so it could
 * neither be seen nor deleted here. Every page is needed: the screen is the
 * account's whole delivery config. After a write, `reload()` it: an
 * invalidation would re-read only the LAST page the walk keyed to.
 */
export function useWebhooks(token: string | null = SESSION_TOKEN): PagedList<ApiWebhook> {
  return usePagedQuery<ApiWebhook>((cursor) => useWebhookPage(cursor, token), { exhaust: true });
}

/**
 * How deep the ledger read goes. The route pages on the shared grammar (default
 * 50), and this page backs BOTH the delivery log and the per-card health stats
 * derived from it — so it asks for the grammar's maximum rather than the
 * default, keeping the stats over the same window they were computed on before
 * the ledger paged. A deeper history is `?filter=webhook_id:` + `?cursor=`,
 * which this screen does not drive yet.
 */
const DELIVERY_PAGE = 100;

/** The account's recent delivery ledger (newest-first, first page). */
export function useWebhookDeliveries(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ApiDelivery[]>(() => ({
    queryKey: [WEBHOOK_DELIVERIES_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () =>
      (
        await apiFetch<Page<ApiDelivery>>(
          `/v1/webhooks/deliveries?per_page=${DELIVERY_PAGE}`,
          token,
        )
      ).data,
  }));
}

/** The create body — name / url / events. The secret is minted server-side. */
export type CreateWebhookBody = {
  name: string;
  url: string;
  events: string[];
};

/** Create a webhook. The plaintext signing `secret` is returned ONCE, here. */
export async function createWebhook(
  body: CreateWebhookBody,
  token: string | null = SESSION_TOKEN,
): Promise<ApiWebhook & { secret: string }> {
  return apiFetch<ApiWebhook & { secret: string }>('/v1/webhooks', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteWebhook(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<{ id: string }>(`/v1/webhooks/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

/** Fire a synthetic signed test delivery — the worker delivers it shortly. */
export async function testWebhook(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<{ delivery: { id: string; status_code: number | null; delivered: boolean } }> {
  return apiFetch(`/v1/webhooks/${encodeURIComponent(id)}/test`, token, { method: 'POST' });
}

/**
 * Whether to offer this caller the webhook writes (test-send). Webhook writes
 * sit in `OWNER_SCOPES` alone (`apps/api/src/middleware/auth.ts`) — an admin runs
 * billing and the fleet, but firing a signed delivery at someone's endpoint is an
 * owner act. The gate itself is `canOwnerWrite` (`lib/roles.ts`), shared with the
 * other owner-only writes so the role list can't drift per feature.
 */
export const canManageWebhooks = canOwnerWrite;

/** What a test-send actually did — a 200 is not proof the endpoint took it. */
export type WebhookTestOutcome = 'delivered' | 'rejected' | 'queued';

/**
 * Grade one test-send. The route answers with the attempt, not a verdict:
 * `delivered` means the receiver 2xx'd it, a recorded `status_code` without
 * delivery means it ANSWERED and refused (retry pending), and no status at all
 * means the attempt is still in flight. Three different things to tell a human,
 * so they don't read "queued for retry" as "your endpoint works".
 */
export function gradeWebhookTest(delivery: {
  status_code: number | null;
  delivered: boolean;
}): WebhookTestOutcome {
  if (delivery.delivered) return 'delivered';
  return delivery.status_code === null ? 'queued' : 'rejected';
}

// ─── raw → card/telemetry mapping ────────────────────────────────────────────

/** A resolved attempt is one the receiver answered (status recorded). */
const isResolved = (d: ApiDelivery): boolean => d.status_code !== null;
const isSuccess = (d: ApiDelivery): boolean =>
  d.status_code !== null && d.status_code >= 200 && d.status_code < 300;
const isRetrying = (d: ApiDelivery): boolean => d.next_retry_at !== null && d.delivered_at === null;

/**
 * Fold each webhook together with its slice of the shared delivery ledger into
 * the card row: health (unhealthy flag wins, else retrying-in-flight, else
 * healthy), 24h success %, last status, retry backlog, and a relative
 * last-delivery. Deliveries arrive newest-first, so `[0]` is the latest.
 *
 * `deliveries` is `undefined` when the LEDGER READ HAS NOT LANDED — in flight,
 * or failed. Every field derived from it then goes absent rather than to a
 * default, because the defaults all read as good news: `?? []` at the call site
 * made `resolved.length === 0` true for every hook, and the old
 * `resolved.length === 0 ? 100` turned that into **100% success** on a health
 * surface, from zero evidence, while a currently-retrying endpoint painted
 * healthy. A landed-but-empty ledger is the same absence of evidence and gets
 * the same answer; the card's own "last delivery: never" is what distinguishes
 * a hook that has not fired from one whose ledger this client cannot see.
 * `healthy` rides the WEBHOOKS read, which did land, so it is still trusted.
 */
export function toCardRows(
  hooks: readonly ApiWebhook[],
  deliveries: readonly ApiDelivery[] | undefined,
  neverLabel: string,
): WebhookRow[] {
  return hooks.map((h) => {
    const mine = (deliveries ?? []).filter((d) => d.webhook_id === h.id);
    const retrying = mine.filter(isRetrying).length;
    const resolved = mine.filter(isResolved);
    const ok = resolved.filter(isSuccess).length;
    const health: WebhookHealth = !h.healthy ? 'unhealthy' : retrying > 0 ? 'retrying' : 'healthy';
    const last = mine[0];
    const lastAt = h.last_delivery_at ?? last?.created_at ?? null;
    return {
      id: h.id,
      name: h.name,
      url_masked: h.url_masked,
      events: h.events,
      last_status_code: last?.status_code ?? null,
      health,
      success_pct: resolved.length === 0 ? null : Math.round((ok / resolved.length) * 100),
      last_delivery: lastAt ? ago(lastAt) : neverLabel,
      ...(retrying > 0 ? { retrying } : {}),
    };
  });
}

/** ISO → `HH:MM:SS.mmm` (UTC) for the delivery-log timestamp column. */
function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(11, 23);
}

/** Flat ledger row → the `DeliveryLog` shape. Latency isn't tracked, so omitted. */
export function toDeliveryRows(deliveries: readonly ApiDelivery[]): Delivery[] {
  return deliveries.map((d) => ({
    id: d.id,
    at: clock(d.created_at),
    event_id: d.event_id,
    type: d.type,
    status_code: d.status_code,
    payload: JSON.stringify(d.payload, null, 2),
  }));
}
